import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Shield, Eye, EyeOff, Sparkles, Copy, Check, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const RULES = [
  { id: "len",    label: "Mínimo 8 caracteres",      test: (p) => p.length >= 8 },
  { id: "upper",  label: "Una letra mayúscula",      test: (p) => /[A-Z]/.test(p) },
  { id: "lower",  label: "Una letra minúscula",      test: (p) => /[a-z]/.test(p) },
  { id: "num",    label: "Un número",                test: (p) => /\d/.test(p) },
  { id: "sym",    label: "Un símbolo especial",      test: (p) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/.test(p) },
];

function strengthScore(p) {
  if (!p) return { score: 0, label: "—", color: "bg-zinc-300" };
  const passed = RULES.filter((r) => r.test(p)).length;
  if (p.length < 8) return { score: 1, label: "Débil", color: "bg-red-500" };
  if (passed <= 3) return { score: 2, label: "Media", color: "bg-amber-500" };
  if (passed === 4) return { score: 3, label: "Fuerte", color: "bg-emerald-500" };
  return { score: 4, label: "Muy fuerte", color: "bg-emerald-600" };
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PasswordChangeCard() {
  const auth = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurr, setShowCurr] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const strength = useMemo(() => strengthScore(next), [next]);
  const allRulesPassed = RULES.every((r) => r.test(next));
  const canSubmit = current && allRulesPassed && next === confirm && !busy;

  const suggest = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/suggest-password`);
      setNext(data.password);
      setConfirm(data.password);
      toast.success("Contraseña sugerida — recuerda copiarla antes de guardar");
    } catch {
      toast.error("No se pudo sugerir");
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(next);
    toast.success("Copiado");
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!canSubmit) return;
    setBusy(true);
    try {
      await axios.post(`${API}/auth/change-password-strong`,
        { current_password: current, new_password: next },
        { headers: auth.apiHeaders() }
      );
      toast.success("Contraseña cambiada · Sesiones cerradas en otros dispositivos");
      setCurrent(""); setNext(""); setConfirm("");
      // Force re-login since the new token will be needed
      setTimeout(() => { auth.logout(); window.location.href = "/login"; }, 1200);
    } catch (ex) {
      const detail = ex?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Error al cambiar contraseña");
    } finally {
      setBusy(false);
    }
  };

  if (!auth?.user || typeof auth.user !== "object") {
    return (
      <section data-testid="password-change-card" className="border hairline bg-cream-card rounded-2xl p-6 md:p-8 mb-8">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-coral">Seguridad</p>
        <h3 className="font-serif text-3xl tracking-tight mt-1">Cambiar contraseña</h3>
        <p className="mt-3 text-sm text-ink-mid">Inicia sesión para cambiar tu contraseña.</p>
      </section>
    );
  }

  return (
    <section data-testid="password-change-card" className="border hairline bg-cream-card rounded-2xl p-6 md:p-8 mb-8">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-coral/15 text-coral flex items-center justify-center">
          <Shield className="w-5 h-5" />
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-coral">Seguridad</p>
          <h3 className="font-serif text-3xl tracking-tight mt-1">Cambiar contraseña</h3>
          <p className="mt-1 text-xs text-ink-mid">Al cambiarla cerramos tus sesiones en otros dispositivos por seguridad.</p>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-3 max-w-lg">
        <Field
          testid="pw-current" label="Contraseña actual"
          value={current} onChange={setCurrent}
          show={showCurr} setShow={setShowCurr}
          autoComplete="current-password"
        />
        <Field
          testid="pw-new" label="Nueva contraseña"
          value={next} onChange={setNext}
          show={showNext} setShow={setShowNext}
          autoComplete="new-password"
          rightSlot={(
            <button type="button" onClick={suggest} data-testid="pw-suggest"
              className="text-[11px] text-coral hover:underline inline-flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Sugerir
            </button>
          )}
        />

        {/* Strength meter */}
        {next && (
          <div data-testid="pw-strength" className="space-y-1.5">
            <div className="flex gap-1">
              {[1, 2, 3, 4].map((b) => (
                <div key={b} className={`h-1.5 flex-1 rounded-full transition ${strength.score >= b ? strength.color : "bg-zinc-200"}`} />
              ))}
            </div>
            <p className="text-[11px] font-mono uppercase tracking-wider text-ink-mid flex justify-between">
              <span>Fuerza:</span>
              <span data-testid="pw-strength-label" className={`${strength.score >= 3 ? "text-emerald-700" : strength.score === 2 ? "text-amber-700" : "text-red-700"}`}>{strength.label}</span>
            </p>
          </div>
        )}

        {/* Rules checklist */}
        <ul className="text-xs space-y-1 mt-2" data-testid="pw-rules">
          {RULES.map((r) => {
            const ok = r.test(next);
            return (
              <li key={r.id} data-testid={`pw-rule-${r.id}`} className={`flex items-center gap-2 ${ok ? "text-emerald-700" : "text-ink-mid"}`}>
                {ok ? <Check className="w-3.5 h-3.5" /> : <span className="w-3.5 h-3.5 rounded-full border border-current opacity-60" />}
                {r.label}
              </li>
            );
          })}
        </ul>

        <Field
          testid="pw-confirm" label="Confirmar nueva contraseña"
          value={confirm} onChange={setConfirm}
          show={showNext} setShow={setShowNext}
          autoComplete="new-password"
        />
        {next && confirm && next !== confirm && (
          <p className="text-xs text-red-700" data-testid="pw-mismatch">Las contraseñas no coinciden.</p>
        )}

        {next && (
          <button type="button" onClick={copy} data-testid="pw-copy"
            className="text-xs text-ink-mid hover:text-coral inline-flex items-center gap-1">
            <Copy className="w-3 h-3" /> Copiar contraseña
          </button>
        )}

        {err && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs" data-testid="pw-error">
            <AlertCircle className="w-3.5 h-3.5" />{err}
          </div>
        )}

        <button type="submit" disabled={!canSubmit}
          data-testid="pw-submit"
          className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-ink text-cream rounded-full py-3 hover:bg-coral transition disabled:opacity-40 disabled:cursor-not-allowed">
          {busy ? "Guardando…" : "Cambiar contraseña"}
        </button>
        <p className="text-[10px] text-ink-mid mt-1">Al guardar cerramos sesiones en otros dispositivos y te enviaremos una notificación de seguridad.</p>
      </form>
    </section>
  );
}

function Field({ testid, label, value, onChange, show, setShow, autoComplete, rightSlot }) {
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-1.5">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid">{label}</span>
        {rightSlot}
      </div>
      <div className="relative">
        <input
          data-testid={testid}
          type={show ? "text" : "password"}
          value={value}
          autoComplete={autoComplete}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-cream border hairline rounded-xl px-3 pr-10 py-2.5 text-sm outline-none focus:border-coral font-mono"
        />
        <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-mid hover:text-coral">
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </label>
  );
}
